import { GenericModelApi } from "./GenericModelApi";
import { ApiClient, ApiError, ApiSchema, FetchResponse } from "./ApiClient";
import { getTenantId } from "@utils/Auth";
import { SettlementFormDataKeys } from "@/generated/SettlementFormDataKeys";
import { SettlementFormData, SettlementRelatedAttributes } from "@/formTypes/SettlementDataType";
import { SettlementProductApi, SettlementProductDataType } from "./SettlementProductApi";
import { SettlementPaymentApi, SettlementPaymentDataType } from "./SettlementPaymentApi";
import { omitProperties } from "@utils/Utils";
import { DocumentAutoGeneratedNumberApi } from "./DocumentAutoGeneratedNumberApi";
import { matchesAmplifyFilter } from "@utils/Api";
import { ProductApi } from "./ProductsApi";
import { saveEDocumentViaLambda } from "./EDocumentSaveApi";

export type SettlementDataType = Partial<ApiSchema["Settlement"]["type"] & SettlementFormData>;

class Api extends GenericModelApi<"Settlement", SettlementDataType> {
    protected userSortKeyDateField = "fecha" as const

    async fetchAll(filters: any = {}, token?: string, limit: number = 20): Promise<FetchResponse<SettlementDataType>> {
        const selectionSet = [
            ...SettlementFormDataKeys.filter(k => !SettlementRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "settlementProducts.*",
            "settlementProducts.product.*",
        ]

        return super.fetchAll(filters, token, limit, selectionSet);
    }

    async fetchAllByRecibe(recibeId: string): Promise<FetchResponse<SettlementDataType>> {
        const fetchFunction = ApiClient.models[this.modelName].listSettlementByRecibeId;

        const collected: SettlementDataType[] = [];

        const { data, errors, nextToken: nt } = await fetchFunction({
            recibeId: recibeId
        }, {
            limit: 1000
        });

        if (errors) {
            throw new ApiError("Error al obtener liquidacion por recibo", "API_ERROR", errors);
        }

        return { items: data };
    }

    async fetchAllForStatus(clientId: string, statusFilter: string, filters: any = {}, token?: string, limit: number = 50): Promise<FetchResponse<SettlementDataType>> {
        const selectionSet = [
            ...SettlementFormDataKeys.filter(k => !SettlementRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "settlementPayments.*",
            "interestEntries.*",
            "settlementInterestEntries.*",
            "recibe.*"
        ];

        const tenantId = await getTenantId();
        const fetchFunction = ApiClient.models[this.modelName].listSettlementByStatusTenant;

        const collected: SettlementDataType[] = [];
        let nextToken: string | undefined = token;

        while (collected.length < limit) {
            const { data, errors, nextToken: nt } = await fetchFunction({
                owner: tenantId,
                status: {
                    eq: statusFilter,
                }
            }, {
                sortDirection: "ASC",
                limit: limit,
                nextToken,
                selectionSet,
            });

            if (errors) {
                throw new ApiError("Error al obtener facturas por tenant", "API_ERROR", errors);
            }

            const pageItems = (data || []).filter((item: SettlementDataType) => matchesAmplifyFilter(item, filters));
            collected.push(...pageItems);

            if (!nt) {
                nextToken = undefined;
                break;
            }
            nextToken = nt;
        }

        return { items: collected, nextToken };
    }

    async fetchAllByPeriod(startData: string, endData: string, filters: any = {}, token?: string, limit: number = 1000): Promise<FetchResponse<SettlementDataType>> {
        const selectionSet = [
            ...SettlementFormDataKeys.filter(k => !SettlementRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "settlementProducts.*",
            "settlementProducts.product.*",
        ]

        return await super.fetchAllByPeriod("listSettlementByFechaTenant", startData, endData, filters, token, {
            selectionSet,
            limit
        })
    }

    async fetchAllByClientDate(clientId: string, endDate: string, filters: any = {}, token?: string, limit: number = 50, sortDirection: "ASC" | "DESC" = "ASC", startDate?: string): Promise<FetchResponse<SettlementDataType>> {
        const selectionSet = [
            ...SettlementFormDataKeys.filter(k => !SettlementRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "settlementPayments.*",
            "interestEntries.*",
            "settlementInterestEntries.*",
            "recibe.*"
        ];

        const tenantId = await getTenantId();
        const fetchFunction = ApiClient.models[this.modelName].listSettlementByClientFechaTenant;

        const fechaFrom = startDate ?? "1900-01-01";
        const fetchProps = {
            owner: tenantId,
            clientIdFecha: {
                between: [
                    {
                        fecha: fechaFrom,
                        clientId,
                    },
                    {
                        fecha: endDate,
                        clientId,
                    }
                ]
            }
        };

        const collected: SettlementDataType[] = [];
        let nextToken: string | undefined = token;

        while (collected.length < limit) {
            const { data, errors, nextToken: nt } = await fetchFunction(fetchProps, {
                sortDirection,
                limit,
                nextToken,
                selectionSet,
            });

            if (errors) {
                throw new ApiError("Error al obtener liquidaciones por cliente y fecha", "API_ERROR", errors);
            }

            const pageItems = (data || []).filter((item: SettlementDataType) => matchesAmplifyFilter(item, filters));
            collected.push(...pageItems);

            if (!nt) {
                nextToken = undefined;
                break;
            }
            nextToken = nt;
        }

        return { items: collected, nextToken };
    }

    /**
     * Guardado transaccional en una sola llamada (lambda saveEDocument).
     */
    async createSettlementAndProducts(input: SettlementDataType, products: SettlementProductDataType[], payments: SettlementPaymentDataType[] = [], reduceInventory: boolean = false): Promise<SettlementDataType> {
        const documentData = {
            ...input,
            status: input.status || "pendiente",
        }

        const lines = products.map((p) => omitProperties({ ...p }, ["id", "forceRecalculatePrice"], true))
        const settlementPayments = payments.map((p) => omitProperties({ ...p }, ["id"], true))

        const data = await saveEDocumentViaLambda<SettlementDataType>({
            documentType: "Settlement",
            document: documentData as Record<string, unknown>,
            lines,
            payments: settlementPayments,
            stockMode: reduceInventory ? "reduce" : "none",
        })

        this.invalidateAllByTenant()
        this.clearCache()
        SettlementProductApi.invalidateAllByTenant()
        SettlementPaymentApi.invalidateAllByTenant()
        ProductApi.invalidateAllByTenant()
        DocumentAutoGeneratedNumberApi.invalidateAllByTenant()

        return data;
    }
}
const api = new Api("Settlement", "numero", "DESC")
api.setCreateDataFieldName("fecha")
export const SettlementApi = api